import styled from '@emotion/styled';
import { Popover } from 'antd';
import type { FC } from 'react';
import type { TrackingIntervalStats } from './types';

const TRACKING_STAT_DEFS: Array<{
  key: keyof TrackingIntervalStats;
  label: string;
  description: string;
}> = [
  {
    key: 'modeIntervalInMs',
    label: 'Mode',
    description: 'Most frequent interval (rounded to nearest second)',
  },
  {
    key: 'minIntervalInMs',
    label: 'Min',
    description: 'Smallest gap between consecutive points',
  },
  { key: 'maxIntervalInMs', label: 'Max', description: 'Largest gap between consecutive points' },
  { key: 'avgIntervalInMs', label: 'Avg', description: 'Arithmetic mean of all intervals' },
  { key: 'medianIntervalInMs', label: 'Median', description: 'Middle value of all intervals' },
  {
    key: 'geomMedianIntervalInMs',
    label: 'Geom. mean',
    description: 'Geometric mean — robust average for skewed distributions',
  },
];

const formatIntervalMs = (ms: number | null): string =>
  ms == null ? '—' : `${(ms / 1000).toFixed(1)}s`;

export const TrackingIntervalBadge: FC<{ stats: TrackingIntervalStats }> = ({ stats }) => {
  const content = (
    <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
      <tbody>
        {TRACKING_STAT_DEFS.map(({ key, label, description }) => (
          <tr key={key}>
            <td style={{ paddingRight: 8, fontWeight: 500, whiteSpace: 'nowrap' }}>{label}</td>
            <td style={{ paddingRight: 12, whiteSpace: 'nowrap' }}>
              {formatIntervalMs(stats[key])}
            </td>
            <td style={{ color: '#888' }}>{description}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  return (
    <Popover
      content={content}
      title="GPS Tracking Intervals"
      trigger={stats.modeIntervalInMs != null ? 'hover' : []}
    >
      <IntervalBadge $active={stats.modeIntervalInMs != null}>
        Tracking Interval: {formatIntervalMs(stats.modeIntervalInMs)}
      </IntervalBadge>
    </Popover>
  );
};

const IntervalBadge = styled.span<{ $active: boolean }>`
  font-size: 11px;
  color: ${({ $active }) => ($active ? '#888' : 'transparent')};
  cursor: ${({ $active }) => ($active ? 'pointer' : 'default')};
  text-decoration: ${({ $active }) => ($active ? 'underline dotted' : 'none')};
  padding-left: 2px;
  user-select: none;
  &:hover {
    color: ${({ $active }) => ($active ? '#333' : 'transparent')};
  }
`;
